import { z } from "zod";
import { PROJECT_FILE_MAX_BYTES, ProjectFileSaveModeSchema } from "../projects.js";
import { RunStreamViewSchema } from "../run-view.js";
import { LocaleSchema } from "../sites.js";
import { CatalogSnapshotSchema, ContentKeySchema, ExplorerUploadResponseSchema, RunEventEnvelopeSchema, RunStreamEndSchema, RunStreamStateSchema, } from "../wire.js";
import { IDEMPOTENCY_KEY_DESCRIPTION, IDEMPOTENCY_KEY_MAX_LENGTH, } from "./access.js";
import { CATALOG_SNAPSHOT_PATH } from "./catalog.js";
function declareRoute(route) {
    return route;
}
const RunIdParam = z.string().uuid().describe("The run: one of the caller's own, on the site.");
const ProjectIdParam = z
    .string()
    .uuid()
    .describe("The project: one of the caller's own, on the site.");
const FileModeQuery = z
    .enum(["preview", "download"])
    .optional()
    .describe("preview (the default) answers inline and at most 256 KiB; download answers the whole file as an attachment.");
const FILE_BYTES_HEADERS = {
    "Content-Type": "The file's stored media type; markup (HTML, XHTML, SVG) is served as text/plain so it never runs as a page.",
    "Content-Disposition": "inline for a preview, attachment for a download, with the file's name.",
    "X-Preview-Truncated": "true when a preview was cut at 256 KiB.",
};
/**
 * The stream's query (`GET /runs/:runId/events`). `from=start`, `until=never` and `view=0` name
 * the defaults; any other value is refused before the first header. Keys it does not know pass,
 * so a later parameter (`include`) is ignored by this server rather than refused.
 */
export const RunStreamQuerySchema = z.object({
    after: z
        .string()
        .optional()
        .describe("Resume after this sequence (empty is no cursor). A value that is not a number is read as an event id, for older clients. Last-Event-ID wins over it."),
    from: z
        .enum(["start", "head"])
        .optional()
        .describe("head starts at the log's head and replays nothing; start (the default) replays it all."),
    until: z
        .enum(["settled", "never"])
        .optional()
        .describe("settled closes the stream with an end frame once the run is terminal and no workflow run is open; never (the default) keeps it open for a follow-up."),
    view: z
        .enum(["0", "1"])
        .optional()
        .describe("1 interleaves view frames (event: view): the run's SessionView, as runs.view answers it, at most one every 250 ms and only when it changed, the first once the replay has caught up (at once with from=head). 0 (the default) sends none."),
});
/** The OpenAPI document as `GET /openapi.json` answers it (contracts/openapi.json, H.14). */
export const OpenApiDocumentSchema = z.looseObject({
    openapi: z.string().describe("The OpenAPI version: 3.1.x."),
    info: z.looseObject({
        title: z.string(),
        version: z.string().describe("WIRE_VERSION, as the v5-api-version header names it."),
    }),
    paths: z.record(z.string(), z.unknown()),
});
/** Every hand-written route the server mounts under API_PREFIX, by name. */
export const API_ROUTES = {
    catalogSnapshot: declareRoute({
        method: "GET",
        path: CATALOG_SNAPSHOT_PATH,
        access: "read",
        public: true,
        summary: "The whole live catalog as one cacheable snapshot",
        description: "Every live template of both sites, the vocabulary they share and the shelves, in the locale (default: the site's), as one GET a browser and any HTTP cache can keep. The templates come in title order; with `shelf` they come in that shelf's order instead: its pinned templates first, then the templates it lists in its Recommended order, then every other live template by title, so each is still there once. It reads no credential. A locale it does not know is 400 invalid_locale, a shelf it does not hold 400 unknown_shelf.",
        params: z.object({}),
        query: z.object({
            locale: LocaleSchema.optional(),
            shelf: ContentKeySchema.optional().describe("A shelf id from `shelves` (a first-party shelf's id is its site id): the templates come in its order."),
        }),
        headers: z.object({
            "if-none-match": z
                .string()
                .optional()
                .describe("An ETag from an earlier answer: 304 with no body while it still matches."),
        }),
        response: {
            status: 200,
            contentType: "application/json",
            description: "The snapshot; 304 when If-None-Match still matches.",
            schema: CatalogSnapshotSchema,
            headers: {
                ETag: "A weak tag that changes with the catalog, the release, the locale and the shelf.",
                "Cache-Control": "public, max-age=60, stale-while-revalidate=600",
                "Content-Encoding": "br or gzip when the request accepts one.",
            },
        },
    }),
    openapiDocument: declareRoute({
        method: "GET",
        path: "/openapi.json",
        access: "read",
        public: true,
        summary: "This document: the wire as OpenAPI 3.1",
        description: "Every procedure and every hand-written route under /api/v5, generated from the contracts and committed as contracts/openapi.json; info.version is the wire's version. It reads no credential and answers on any host and to any origin, with an ETag of its content (304 on If-None-Match).",
        params: z.object({}),
        headers: z.object({
            "if-none-match": z
                .string()
                .optional()
                .describe("An ETag from an earlier answer: 304 with no body while it still matches."),
        }),
        response: {
            status: 200,
            contentType: "application/json",
            description: "The document; 304 when If-None-Match still matches.",
            schema: OpenApiDocumentSchema,
            headers: {
                ETag: "A weak tag of the document's sha256: it changes exactly when the document does.",
                "Cache-Control": "public, max-age=300",
                "Access-Control-Allow-Origin": "*: any origin may read it.",
                "Content-Encoding": "br or gzip when the request accepts one.",
            },
        },
    }),
    runEvents: declareRoute({
        method: "GET",
        path: "/runs/:runId/events",
        access: "read",
        summary: "A run's live event stream (SSE, Last-Event-ID)",
        description: "The run's durable event log as server-sent events: a state frame first, then every envelope in ascending sequence with its sequence as the SSE id, with ?view=1 view frames between them, a ': ping' comment every 15 seconds, and with ?until=settled an end frame last. It starts after Last-Event-ID, else after ?after=, else at the head with ?from=head, else from the start; frames arrive at least once across reconnects, so a client dedupes by sequence. Refusals come before any header, as the one error body at their status (404 run_not_found, 401 authentication_required, 400 BAD_REQUEST for a query value it does not know, the key refusals). A revoked key or session ends the stream at its next heartbeat.",
        params: z.object({ runId: RunIdParam }),
        query: RunStreamQuerySchema,
        headers: z.object({
            "last-event-id": z
                .string()
                .optional()
                .describe("The last sequence received: the stream resumes after it."),
        }),
        response: {
            status: 200,
            contentType: "text/event-stream",
            description: "Server-sent events until the client closes, or the end frame.",
            frames: {
                state: {
                    id: false,
                    description: "First, once (event: state): the wire version, the log's head when the stream opened (-1 when empty) and the run's status then.",
                    schema: RunStreamStateSchema,
                },
                message: {
                    id: true,
                    description: "Each durable envelope in ascending sequence, with no event: line; its SSE id: is the envelope's sequence.",
                    schema: RunEventEnvelopeSchema,
                },
                view: {
                    id: false,
                    description: "Only with ?view=1 (event: view): the run's SessionView as runs.view answers it, with the last log sequence folded into it, never past the last envelope this connection wrote. At most one every 250 ms, and only when the view changed since this connection's last; the first comes once the replay has caught up, or at once with ?from=head. A queued message's processed copy reaches the view (it leaves queued) with no envelope of its own. With ?until=settled, the last one comes before the end frame.",
                    schema: RunStreamViewSchema,
                },
                end: {
                    id: false,
                    description: "Only with ?until=settled, once and last (event: end): the terminal status and the sequence the stream read through.",
                    schema: RunStreamEndSchema,
                },
            },
        },
    }),
    runOutputContent: declareRoute({
        method: "GET",
        path: "/runs/:runId/outputs/content",
        access: "read",
        summary: "One deliverable's bytes (?path=&mode=preview|download)",
        description: "A run's deliverable, text or binary, by its path exactly as runs.outputs lists it. A missing path is 400 path_required; an unknown run or path is 404.",
        params: z.object({ runId: RunIdParam }),
        query: z.object({
            path: z.string().min(1).describe("The deliverable's path as runs.outputs lists it."),
            mode: FileModeQuery,
        }),
        response: {
            status: 200,
            contentType: "application/octet-stream",
            description: "The deliverable's bytes.",
            headers: FILE_BYTES_HEADERS,
        },
    }),
    projectUpload: declareRoute({
        method: "POST",
        path: "/projects/:projectId/explorer/uploads",
        access: "write",
        summary: "Upload a file to a project (multipart)",
        description: `Stores one file in the project, mounted at the next run's start. A save creates by default: a name the project already holds is 409 file_exists and nothing is written, unless mode is replace, which replaces that file; any other mode is 422 mode_invalid. More than ${PROJECT_FILE_MAX_BYTES} bytes is 413 file_too_large; a body that is not multipart is 415 multipart_required, or 400 multipart_invalid; a field sent more than once is 400 form_field_repeated, with data {field}, and nothing is written; no file field is 422 file_required; an unusable name is 422 file_name_invalid; a project at its file limit is 422 too_many_files.`,
        params: z.object({ projectId: ProjectIdParam }),
        headers: z.object({
            "idempotency-key": z
                .string()
                .min(1)
                .max(IDEMPOTENCY_KEY_MAX_LENGTH)
                .optional()
                .describe(`${IDEMPOTENCY_KEY_DESCRIPTION} An upload is the same request when its fields and its file (name, media type and bytes) are.`),
        }),
        body: {
            contentType: "multipart/form-data",
            schema: z.object({
                file: z
                    .file()
                    .max(PROJECT_FILE_MAX_BYTES)
                    .describe("The file. Its name and media type are kept with it."),
                destination: z
                    .enum(["upload", "user_artifact"])
                    .optional()
                    .describe("Accepted and not read: V5 keeps one kind of project file."),
                mode: ProjectFileSaveModeSchema.optional().describe("create (the default) refuses a name the project already holds with 409 file_exists; replace replaces that file."),
                targetRunId: z
                    .string()
                    .optional()
                    .describe("Accepted and not read: an upload mounts at the next run's start."),
            }),
        },
        response: {
            status: 201,
            contentType: "application/json",
            description: "The stored file as an explorer entry; file.id is its file:<uuid> id.",
            schema: ExplorerUploadResponseSchema,
            headers: {
                "Idempotency-Replayed": "true on an answer replayed for a retry with the same Idempotency-Key.",
            },
        },
    }),
    projectEntryContent: declareRoute({
        method: "GET",
        path: "/projects/:projectId/explorer/entries/:entryKey/content",
        access: "read",
        summary: "One project upload's bytes (?mode=preview|download)",
        description: "An uploaded file's bytes by its entry key. An unknown project or entry is 404.",
        params: z.object({
            projectId: ProjectIdParam,
            entryKey: z.string().min(1).describe("The upload's entry key (file:<uuid>)."),
        }),
        query: z.object({ mode: FileModeQuery }),
        response: {
            status: 200,
            contentType: "application/octet-stream",
            description: "The file's bytes.",
            headers: FILE_BYTES_HEADERS,
        },
    }),
};
/**
 * A declared route's URL path relative to API_PREFIX: each `:name` segment filled from
 * `params`, URL-encoded, and `query` appended in the order given, an undefined value left out.
 * Building every hand-written URL through this keeps a client to the paths the server mounts.
 */
export function routePath(route, params, query = {}) {
    const values = params;
    const path = route.path.replace(/:([A-Za-z][A-Za-z0-9]*)/g, (_, name) => {
        const value = values[name];
        if (!value)
            throw new TypeError(`${route.path} needs :${name}`);
        return encodeURIComponent(value);
    });
    const search = Object.entries(query)
        .flatMap(([key, value]) => value === undefined ? [] : [`${encodeURIComponent(key)}=${encodeURIComponent(value)}`])
        .join("&");
    return search ? `${path}?${search}` : path;
}
/** Path segments as Express matches them: a trailing slash ignored. */
function segmentsOf(path) {
    return path.replace(/\/+$/, "").split("/").slice(1);
}
/**
 * The declared route a request is, with its decoded params, or null when it is none. It
 * matches as Express does: HEAD as GET, a trailing slash ignored, letters in any case.
 * `path` is relative to API_PREFIX and carries no query.
 */
export function matchApiRoute(method, path) {
    const verb = method.toUpperCase() === "HEAD" ? "GET" : method.toUpperCase();
    const segments = segmentsOf(path);
    for (const [name, route] of Object.entries(API_ROUTES)) {
        if (route.method !== verb)
            continue;
        const pattern = segmentsOf(route.path);
        if (pattern.length !== segments.length)
            continue;
        const params = {};
        const matched = pattern.every((part, index) => {
            const segment = segments[index] ?? "";
            if (!part.startsWith(":"))
                return part.toLowerCase() === segment.toLowerCase();
            if (!segment)
                return false;
            try {
                params[part.slice(1)] = decodeURIComponent(segment);
                return true;
            }
            catch {
                return false;
            }
        });
        if (matched)
            return { name, route, params };
    }
    return null;
}
//# sourceMappingURL=routes.js.map